import { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights, Sizes } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';
import { IStyles, mediaDesktop, mediaNoDesktop } from '../../utils/styles';

export interface IAppBarClassNames {
  root: string;
  logo: string;
  menuIcon: string;
  title: string;
  content: string;
  contentContainer?: string;
  menu?: string;
  children?: string;
  childrenContent?: string;
  userMenu: string;
}

export type IAppBarStyleKeys =
  | 'root'
  | 'logo'
  | 'menuIcon'
  | 'title'
  | 'content'
  | 'contentContainer'
  | 'menu'
  | 'children'
  | 'childrenContent'
  | 'userMenu';
export type IAppBarStyles = IStyles<IAppBarStyleKeys>;

export type NavigationStyleKeys = 'root';
export type NavigationStyles = IStyles<NavigationStyleKeys>;

const flexCenter = { display: 'flex', flexDirection: 'column', justifyContent: 'center' };

export const appBarStylesDefault = (theme: IH2OTheme): IAppBarStyles => ({
  root: {
    display: 'flex',
    width: '100%',
    height: 60,
    background: theme.palette?.white,
    boxShadow: `${theme.palette?.gray400_a_16} 0 3px 6px`,
  },
  logo: {
    cursor: 'pointer',
    paddingLeft: 32,
    paddingRight: 40,
    ...flexCenter,
  },
  menuIcon: {
    paddingLeft: 24,
    paddingRight: 40,
    ...flexCenter,
  },
  title: {
    cursor: 'pointer',
    color: theme.palette?.primary800,
    fontWeight: FontWeights.semiBold,
    fontSize: FontSizes.xlarge,
    ...flexCenter,
  },
  content: { flex: 1, ...flexCenter },
  contentContainer: {
    display: 'flex',
  },
  menu: {
    flex: 1,
    ...CommonStyles.flexCenterVertically,
  },
  children: {
    height: '100%',
    ...CommonStyles.flexCenterVertically,
  },
  childrenContent: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  userMenu: {
    paddingLeft: 32,
    paddingRight: 8,
    ...flexCenter,
  },
});

export const appBarStylesThick: IAppBarStyles = {
  root: {
    height: 96,
    userMenu: {
      paddingRight: Sizes.padding,
    },
  },
};

export const appBarStylesResponsive = (theme: IH2OTheme): NavigationStyles => ({
  root: {
    ...(appBarStylesThick.root as object),
    ...mediaNoDesktop({
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: '10 !important',
      height: '60px',
      padding: '0 10px',
    }),

    // MainNav render control
    '&.main-nav-show': {
      '.h2o-AppBar-content, .h2o-AppBar-userMenu': { ...mediaNoDesktop({ transform: 'translate3d(0, 0, 0)' }) },
      '.h2o-AppBar-logo': { ...mediaNoDesktop({ transform: 'translate3d(-43px, 0, 0)' }) },
      'div.ms-Image:after': {
        ...mediaNoDesktop({
          transform: 'translate3d(0, -50%, 0)',
          opacity: 1,
        }),
      },
      '.h2o-AppBar-menuIcon': {
        ...mediaNoDesktop({ transform: 'translate3d(290px, 0, 0)' }),
        'button:after': { ...mediaNoDesktop({ transform: 'translate3d(-50%, -50%, 0) scale(1.5)' }) },
        '.ms-Button-icon': {
          ...mediaNoDesktop({
            transform: 'scale(0)',
            transition: 'transform .3s',
          }),
        },
      },
    },

    // Header
    '.h2o-AppBar-logo': {
      ...mediaNoDesktop({
        zIndex: 2,
        padding: '0 5px',
        transition: 'transform .3s',
      }),
      '.ms-Image': {
        ...mediaNoDesktop({
          position: 'relative',
          overflow: 'visible',
        }),
      },
      img: { ...mediaNoDesktop({ height: 36 }) },
    },
    '.h2o-AppBar-menuIcon': {
      ...mediaNoDesktop({
        position: 'relative',
        left: 0,
        order: -1,
        zIndex: 2,
        padding: '0 8px',
        transition: 'transform .3s',
      }),
      ...mediaDesktop({ display: 'none' }),

      '.ms-Button-icon': {
        ...mediaNoDesktop({
          transform: 'scale(1)',
          transition: 'transform .3s',
        }),
      },

      'button:after': {
        ...mediaNoDesktop({
          content: '""',
          background:
            'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0iY3VycmVudENvbG9yIiBjbGFzcz0iYmkgYmkteC1sZyIgdmlld0JveD0iMCAwIDE2IDE2Ij4KICA8cGF0aCBkPSJNMi4xNDYgMi44NTRhLjUuNSAwIDEgMSAuNzA4LS43MDhMOCA3LjI5M2w1LjE0Ni01LjE0N2EuNS41IDAgMCAxIC43MDguNzA4TDguNzA3IDhsNS4xNDcgNS4xNDZhLjUuNSAwIDAgMS0uNzA4LjcwOEw4IDguNzA3bC01LjE0NiA1LjE0N2EuNS41IDAgMCAxLS43MDgtLjcwOEw3LjI5MyA4eiIvPgo8L3N2Zz4=") no-repeat fixed center',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          transform: 'translate3d(-50%, -50%, 0) scale(0)',
          transition: 'transform .3s',
        }),
      },
    },

    // Main Nav
    '.h2o-AppBar-content, .h2o-AppBar-userMenu': {
      ...mediaNoDesktop({
        position: 'fixed',
        left: 0,
        zIndex: 1,
        width: 352,
        backgroundColor: theme.palette?.white,
        transition: 'transform .3s',
        transform: 'translate3d(-100%, 0, 0)',
      }),
    },
    '.h2o-AppBar-content': {
      ...mediaNoDesktop({
        top: 0,
        height: '90%',
        justifyContent: 'flex-start',
        padding: '80px 17px',
      }),
    },
    '.h2o-AppBar-contentContainer': {
      ...mediaNoDesktop({ flexDirection: 'column' }),
      '#beamerButton': {
        ...mediaNoDesktop({
          width: 'auto',
          display: 'flex',
          alignItems: 'center',
          marginRight: 'auto',
          padding: 0,
        }),
      },
      '#beamerButton:after': {
        ...mediaNoDesktop({
          content: 'attr(title)',
          fontSize: FontSizes.large,
          lineHeight: FontSizes.xlarge,
          color: theme.palette?.gray600,
        }),
      },
    },
    'div.h2o-BasicList-root': {
      ...mediaNoDesktop({
        flexFlow: 'column',
        marginBottom: '95%',
      }),
      a: {
        ...mediaNoDesktop({
          fontSize: FontSizes.large,
          lineHeight: FontSizes.xlarge,
          display: 'block',
          textTransform: 'none',
          padding: '12px 0',
        }),
      },
      'a.active': {
        ...mediaNoDesktop({
          fontWeight: FontWeights.semiBold,
          color: theme.palette?.primary900,
        }),
      },
    },
    '.h2o-Item-root': {
      ...mediaNoDesktop({
        display: 'block',
        marginRight: 0,
      }),
    },
    '.h2o-Item-labelButton': { ...mediaNoDesktop({ display: 'block' }) },
    'div.h2o-BasicList-root .h2o-BasicList-item a.active:after': { ...mediaNoDesktop({ display: 'none' }) },

    // User Menu
    '.h2o-AppBar-userMenu': {
      ...mediaNoDesktop({
        padding: 0,
        bottom: 0,
        height: '10%',
      }),
      '.ms-layer': { ...mediaNoDesktop({ display: 'none' }) },
    },
    '.h2o-AppBar-userMenu button': { ...mediaNoDesktop({ width: '100%' }) },
    '.h2o-AppBar-userMenu .ms-Button-flexContainer': {
      ...mediaNoDesktop({
        justifyContent: 'space-between',
        alignItems: 'center',
      }),
    },
  },
});
